import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import logo from '../../imports/logo-cuadrado.png'
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    ArrowLeft,
    Building2,
    ShieldCheck,
    LogIn,
    Store,
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { FormError } from '../components/ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp'
import { REGEXP_ONLY_DIGITS } from 'input-otp'

import { useSessionStore, useDeviceStore, type AuthUser } from '../store/auth/auth.store'
import { authService } from '../services/auth.service'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDeviceName(): string {
    const ua = navigator.userAgent
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone / iPad'
    if (/Android/i.test(ua)) return 'Dispositivo Android'
    if (/Mac/i.test(ua)) return 'Mac'
    if (/Win/i.test(ua)) return 'Windows PC'
    return 'Navegador Web'
}

function getOrCreateDeviceUUID(): string {
    const KEY = 'device_uuid'

    const existing = localStorage.getItem(KEY)

    if (existing) {
        return existing
    }

    const uuid =
        typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : generateUUID()

    console.log(uuid)
    localStorage.setItem(KEY, uuid)

    return uuid
}

function generateUUID(): string {
    return '10000000-1000-4000-8000-100000000000'.replace(
        /[018]/g,
        (c) =>
            (
                Number(c) ^
                (crypto.getRandomValues(new Uint8Array(1))[0] &
                    (15 >> (Number(c) / 4)))
            ).toString(16)
    )
}

function getInitials(name: string): string {
    return name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('')
}

const AVATAR_COLORS = [
    ['#3B7D3B', '#d1fae5'],
    ['#2563eb', '#dbeafe'],
    ['#7c3aed', '#ede9fe'],
    ['#db2777', '#fce7f3'],
    ['#d97706', '#fef3c7'],
    ['#0891b2', '#cffafe'],
]

function avatarColor(userId: number) {
    return AVATAR_COLORS[userId % AVATAR_COLORS.length]
}

// ─── Schemas ──────────────────────────────────────────────────────────────────

const firstLoginSchema = z.object({
    email: z.string().email('Ingresa un correo válido'),
    password: z.string().min(1, 'La contraseña es requerida'),
    sucursal_id: z.string().refine(val => Number(val) > 0, 'Debes seleccionar una sucursal'),
})
type FirstLoginForm = z.infer<typeof firstLoginSchema>

const credentialLoginSchema = z.object({
    password: z.string().min(1, 'La contraseña es requerida'),
})
type CredentialLoginForm = z.infer<typeof credentialLoginSchema>

// ─── Sub-views ────────────────────────────────────────────────────────────────

// ── 1. First-time / Admin login view ─────────────────────────────────────────

function FirstLoginView({ onSuccess }: { onSuccess: () => void }) {
    const [showPassword, setShowPassword] = useState(false)
    const { setToken, setUser } = useSessionStore()
    const { setTrustedDevice } = useDeviceStore()
    const [sucursales, setSucursales] = useState<{id: number, name: string}[]>([])

    useEffect(() => {
        authService.getSucursales()
            .then(setSucursales)
            .catch(() => toast.error('No se pudieron cargar las sucursales'))
    }, [])

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<FirstLoginForm>({
        resolver: zodResolver(firstLoginSchema),
        defaultValues: { email: '', password: '', sucursal_id: '0' },
    })

    const onSubmit = async (data: FirstLoginForm) => {
        try {
            const device_uuid = getOrCreateDeviceUUID()
            const device_name = getDeviceName()
            const branch_id = Number(data.sucursal_id)

            const res = await authService.login({
                email: data.email,
                password: data.password,
                device_uuid,
                device_name,
                sucursal_id: branch_id,
            })

            setToken(res.token)
            setUser(res.user)
            setTrustedDevice({
                device_uuid,
                device_name,
                sucursal_id: branch_id,
            })

            toast.success(`Bienvenido, ${res.user.name}`)
            onSuccess()
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ?? 'Credenciales incorrectas. Verifica tus datos.'
            console.log(getOrCreateDeviceUUID())
            toast.error(msg)
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                    Sucursal a configurar
                </label>
                <div className="relative">
                    <Store className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <select
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 pl-9 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        {...register('sucursal_id')}
                    >
                        <option value="0" disabled>Selecciona una sucursal...</option>
                        {sucursales.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
                <FormError message={errors.sucursal_id?.message} />
            </div>

            <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                    Correo electrónico
                </label>
                <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        type="email"
                        placeholder="admin@farmaciassaludplus.local"
                        className="pl-9"
                        {...register('email')}
                    />
                </div>
                <FormError message={errors.email?.message} />
            </div>

            <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                    Contraseña
                </label>
                <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        className="pl-9 pr-10"
                        {...register('password')}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
                <FormError message={errors.password?.message} />
            </div>


            <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#3B7D3B] hover:bg-[#2d6a2d] h-11 text-base"
            >
                {isSubmitting ? (
                    <Spinner className="h-4 w-4 mr-2" />
                ) : (
                    <LogIn className="mr-2 h-4 w-4" />
                )}
                Iniciar sesión
            </Button>
        </form>
    )
}

// ── 2. Avatar picker (trusted device) ────────────────────────────────────────

interface AvatarPickerViewProps {
    users: AuthUser[]
    onSelectUser: (user: AuthUser) => void
    isLoading: boolean
}

function AvatarPickerView({ users, onSelectUser, isLoading }: AvatarPickerViewProps) {
    if (isLoading) {
        return (
            <div className="flex h-40 items-center justify-center">
                <Spinner className="h-8 w-8 text-[#3B7D3B]" />
            </div>
        )
    }

    if (users.length === 0) {
        return (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-slate-400">
                <Building2 className="h-10 w-10" />
                <p className="text-sm">No hay usuarios disponibles en este dispositivo</p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <p className="text-center text-sm text-slate-500">
                Selecciona tu perfil para continuar
            </p>
            <div className="grid grid-cols-3 gap-4">
                {users.map((user) => {
                    const [fg, bg] = avatarColor(user.id)
                    return (
                        <button
                            key={user.id}
                            onClick={() => onSelectUser(user)}
                            className="group flex flex-col items-center gap-2.5 rounded-xl p-3 transition-all hover:bg-slate-50 active:scale-95"
                        >
                            <div
                                className="flex h-16 w-16 items-center justify-center rounded-2xl text-lg font-bold shadow-sm ring-2 ring-transparent transition-all group-hover:ring-[#3B7D3B] group-hover:shadow-md"
                                style={{ backgroundColor: bg, color: fg }}
                            >
                                {user.avatar_url ? (
                                    <img
                                        src={user.avatar_url}
                                        alt={user.name}
                                        className="h-full w-full rounded-2xl object-cover"
                                    />
                                ) : (
                                    getInitials(user.name)
                                )}
                            </div>
                            <span className="max-w-full truncate text-xs font-medium text-slate-700">
                                {user.name.split(' ')[0]}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

// ── 3. PIN Login view ─────────────────────────────────────────────────────────

interface PinLoginViewProps {
    user: AuthUser
    onBack: () => void
    onSuccess: () => void
}

function PinLoginView({ user, onBack, onSuccess }: PinLoginViewProps) {
    const [pin, setPin] = useState('')
    const [loading, setLoading] = useState(false)
    const { setToken, setUser } = useSessionStore()
    const { trustedDevice } = useDeviceStore()
    const [fg, bg] = avatarColor(user.id)

    const handlePinComplete = async (value: string) => {
        if (!trustedDevice) return
        setLoading(true)
        try {
            const res = await authService.pinLogin({
                user_id: user.id,
                pin: value,
                device_uuid: trustedDevice.device_uuid,
                sucursal_id: trustedDevice.sucursal_id,
            })
            setToken(res.token)
            setUser(res.user)
            toast.success(`¡Bienvenido, ${res.user.name}!`)
            onSuccess()
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? 'PIN incorrecto'
            toast.error(msg)
            setPin('')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
                <ArrowLeft className="h-4 w-4" />
                Cambiar usuario
            </button>

            <div className="flex flex-col items-center gap-3">
                <div
                    className="flex h-20 w-20 items-center justify-center rounded-2xl text-2xl font-bold shadow-md"
                    style={{ backgroundColor: bg, color: fg }}
                >
                    {user.avatar_url ? (
                        <img
                            src={user.avatar_url}
                            alt={user.name}
                            className="h-full w-full rounded-2xl object-cover"
                        />
                    ) : (
                        getInitials(user.name)
                    )}
                </div>
                <div className="text-center">
                    <p className="font-semibold text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-400">Ingresa tu PIN de acceso</p>
                </div>
            </div>

            <div className="flex flex-col items-center gap-4">
                <InputOTP
                    maxLength={4}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={pin}
                    onChange={setPin}
                    onComplete={handlePinComplete}
                    disabled={loading}
                >
                    <InputOTPGroup className="gap-2">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <InputOTPSlot
                                key={i}
                                index={i}
                                className="h-12 w-12 rounded-lg border-slate-300 text-lg font-bold"
                            />
                        ))}
                    </InputOTPGroup>
                </InputOTP>

                {loading && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Spinner className="h-4 w-4 text-[#3B7D3B]" />
                        Verificando PIN...
                    </div>
                )}
            </div>
        </div>
    )
}

// ── 4. Credential Login view (trusted device, use_pin_login = false) ──────────

interface CredentialLoginViewProps {
    user: AuthUser
    onBack: () => void
    onSuccess: () => void
}

function CredentialLoginView({ user, onBack, onSuccess }: CredentialLoginViewProps) {
    const [showPassword, setShowPassword] = useState(false)
    const { setToken, setUser } = useSessionStore()
    const { trustedDevice } = useDeviceStore()
    const [fg, bg] = avatarColor(user.id)

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<CredentialLoginForm>({
        resolver: zodResolver(credentialLoginSchema),
        defaultValues: { password: '' },
    })

    const onSubmit = async (data: CredentialLoginForm) => {
        if (!trustedDevice) return
        try {
            const res = await authService.credentialLogin({
                user_id: user.id,
                email: user.email,
                password: data.password,
                device_uuid: trustedDevice.device_uuid,
                sucursal_id: trustedDevice.sucursal_id,
            })
            setToken(res.token)
            setUser(res.user)
            toast.success(`¡Bienvenido, ${res.user.name}!`)
            onSuccess()
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? 'Contraseña incorrecta'
            toast.error(msg)
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
                <ArrowLeft className="h-4 w-4" />
                Cambiar usuario
            </button>

            <div className="flex flex-col items-center gap-3">
                <div
                    className="flex h-20 w-20 items-center justify-center rounded-2xl text-2xl font-bold shadow-md"
                    style={{ backgroundColor: bg, color: fg }}
                >
                    {user.avatar_url ? (
                        <img
                            src={user.avatar_url}
                            alt={user.name}
                            className="h-full w-full rounded-2xl object-cover"
                        />
                    ) : (
                        getInitials(user.name)
                    )}
                </div>
                <div className="text-center">
                    <p className="font-semibold text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-400">{user.email}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Contraseña</label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            className="pl-9 pr-10"
                            autoFocus
                            {...register('password')}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                    <FormError message={errors.password?.message} />
                </div>

                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#3B7D3B] hover:bg-[#2d6a2d] h-11 text-base"
                >
                    {isSubmitting ? <Spinner className="h-4 w-4 mr-2" /> : <LogIn className="mr-2 h-4 w-4" />}
                    Entrar
                </Button>
            </form>
        </div>
    )
}

// ─── View types ───────────────────────────────────────────────────────────────

type LoginView =
    | 'first-login'          // No device registered → email + password (admin/initial)
    | 'avatar-picker'        // Trusted device → select user avatar
    | 'pin-login'            // Selected use_pin_login = true user
    | 'credential-login'     // Selected use_pin_login = false user

// ─── Main Login Component ─────────────────────────────────────────────────────

export function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
    const { trustedDevice } = useDeviceStore()

    const [view, setView] = useState<LoginView>(
        trustedDevice ? 'avatar-picker' : 'first-login',
    )
    const [users, setUsers] = useState<AuthUser[]>([])
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null)

    // ── Fetch users for trusted device ────────────────────────────────────────

    useEffect(() => {
        if (view === 'avatar-picker' && trustedDevice) {
            setLoadingUsers(true)
            authService
                .loginUsers({
                    device_uuid: trustedDevice.device_uuid,
                    sucursal_id: trustedDevice.sucursal_id,
                })
                .then(setUsers)
                .catch(() => {
                    toast.error('No se pudo obtener la lista de usuarios')
                    setUsers([])
                })
                .finally(() => setLoadingUsers(false))
        }
    }, [view, trustedDevice])

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleFirstLoginSuccess = () => {
        onAuthenticated()
    }

    const handleSelectUser = (user: AuthUser) => {
        setSelectedUser(user)
        if (user.use_pin_login) {
            setView('pin-login')
        } else {
            setView('credential-login')
        }
    }

    const handleBackToAvatars = () => {
        setSelectedUser(null)
        setView('avatar-picker')
    }

    // ── Title per view ────────────────────────────────────────────────────────

    const titles: Record<LoginView, { heading: string; sub: string }> = {
        'first-login': {
            heading: 'Bienvenido al sistema',
            sub: 'Inicia sesión para registrar este dispositivo como punto de venta.',
        },
        'avatar-picker': {
            heading: '¿Quién eres?',
            sub: 'Selecciona tu perfil para acceder al sistema.',
        },
        'pin-login': {
            heading: 'Ingresa tu PIN',
            sub: 'Usa tu código de acceso personal de 4 dígitos.',
        },
        'credential-login': {
            heading: 'Ingresa tu contraseña',
            sub: 'Este usuario requiere contraseña para acceder.',
        },
    }

    const { heading, sub } = titles[view]

    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50 p-4">
            {/* Decorative blobs */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed -top-32 -left-32 h-[500px] w-[500px] rounded-full opacity-20 blur-3xl"
                style={{ background: 'radial-gradient(circle, #3B7D3B 0%, transparent 70%)' }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none fixed -bottom-32 -right-32 h-[500px] w-[500px] rounded-full opacity-10 blur-3xl"
                style={{ background: 'radial-gradient(circle, #10b981 0%, transparent 70%)' }}
            />

            {/* Card */}
            <div className="relative z-10 w-full max-w-sm">
                {/* Logo area */}
                <div className="mb-3 flex flex-col items-center">
                    <img
                        src={logo}
                        alt="Farmacias Salud Plus"
                        className="h-40 w-auto object-contain drop-shadow-sm"
                    />

                    <div className="mt-4 flex items-center gap-2 rounded-full bg-green-50 px-3 py-1">
                        {view === 'first-login' ? (
                            <LogIn className="h-4 w-4 text-[#3B7D3B]" />
                        ) : view === 'avatar-picker' ? (
                            <ShieldCheck className="h-4 w-4 text-[#3B7D3B]" />
                        ) : (
                            <Lock className="h-4 w-4 text-[#3B7D3B]" />
                        )}

                        <span className="text-xs font-medium text-[#3B7D3B]">
                            Sistema Punto de Venta
                        </span>
                    </div>
                </div>

                {/* Panel */}
                <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-sm">
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-slate-900">{heading}</h2>
                        <p className="mt-1 text-sm text-slate-500">{sub}</p>
                    </div>

                    {/* ── Views ── */}
                    {view === 'first-login' && (
                        <FirstLoginView onSuccess={handleFirstLoginSuccess} />
                    )}

                    {view === 'avatar-picker' && (
                        <>
                            <AvatarPickerView
                                users={users}
                                onSelectUser={handleSelectUser}
                                isLoading={loadingUsers}
                            />
                            {/* Fallback: use email login to register new trusted device */}
                            <div className="mt-6 border-t border-slate-100 pt-4 text-center">
                                <button
                                    onClick={() => setView('first-login')}
                                    className="text-xs text-slate-400 underline-offset-4 hover:text-slate-600 hover:underline transition-colors"
                                >
                                    Soy administrador · Iniciar con correo y contraseña
                                </button>
                            </div>
                        </>
                    )}

                    {view === 'pin-login' && selectedUser && (
                        <PinLoginView
                            user={selectedUser}
                            onBack={handleBackToAvatars}
                            onSuccess={onAuthenticated}
                        />
                    )}

                    {view === 'credential-login' && selectedUser && (
                        <CredentialLoginView
                            user={selectedUser}
                            onBack={handleBackToAvatars}
                            onSuccess={onAuthenticated}
                        />
                    )}
                </div>

                {/* Footer */}
                <p className="mt-5 text-center text-xs text-slate-400">
                    © {new Date().getFullYear()} Farmacias Salud Plus · v1.0
                </p>
            </div>
        </div>
    )
}
